import { QuoteIcon } from '@jouzen/ecom-components/icons/QuoteIcon';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { getImageProps } from 'next/image';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import { useImageExperience } from '@/app/hooks/useImageExperience';

import MobileFeature from './MobileFeature';

const StartDay = () => {
  const t = useTranslations();
  const imageExperience = useImageExperience({
    japaneseImgObj: {
      img: 'ja-regional/hp-features-start-your-day-desktop',
      alt: 'man_stomach_img_alt',
    },
    jadeImgObj: {
      img: 'features/starting_the_day_jade',
      alt: 'stretch_hand_img_alt',
    },
    defaultImgObj: {
      img: 'blue-sky/home/start-day',
      alt: 'tab_item_start_day_img_aria_label',
    },
  });

  const { props: sleepChartMaskImageProps } = getImageProps({
    alt: '',
    height: 228,
    src: `jade/masks/bg_mask_starting_your_day_asleep.jpg`,
    width: 300,
  });

  const { props: readinessMaskImageProps } = getImageProps({
    alt: '',
    height: 270,
    src: `jade/masks/bg_mask_starting_your_day_readiness.jpg`,
    width: 300,
  });

  return (
    <MobileFeature
      title="features_tab_1_label"
      image={{
        alt: imageExperience.alt,
        src: imageExperience.img,
      }}
    >
      <div className="relative mt-14 hidden lg:block" data-cy="start-day">
        <Image
          alt={t(imageExperience.alt)}
          src={imageExperience.img}
          width={520}
          height={520}
          className="rounded-lg object-cover"
          data-cy="start-day-image"
        />
      </div>
      <div
        className={cx(
          'absolute bottom-16 left-1/2 w-[80vw] max-w-[300px] -translate-x-1/2 flex-col gap-y-6',
          'lg:relative lg:bottom-auto lg:left-0 lg:flex lg:w-auto lg:max-w-none lg:translate-x-0',
        )}
      >
        <div
          className="hidden grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:grid"
          style={{ backgroundImage: `url(${sleepChartMaskImageProps.src})` }}
          data-cy="start-day-mask-image-asleep"
        >
          <Image
            localizedAlt="sleep_app_image_alt"
            className="col-start-1 col-end-2 row-start-1 row-end-2 px-4"
            localizedSrc="home_scenarios_sleep_chart_ui"
            format="png"
            width={300}
            height={228}
            data-cy="start-day-sleep-chart-image"
          />
        </div>
        <div
          className="hidden grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:grid"
          style={{ backgroundImage: `url(${readinessMaskImageProps.src})` }}
          data-cy="start-day-mask-image-readiness"
        >
          <Image
            localizedAlt="readiness_app_image_alt"
            className="col-start-1 col-end-2 row-start-1 row-end-2 pt-4"
            localizedSrc="home_scenarios_sleep_readiness_ui"
            format="png"
            width={300}
            height={270}
            data-cy="start-day-readiness-image"
          />
        </div>
        <Motion
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, type: 'tween', delay: 0.5 }}
          className="grid grid-cols-1 grid-rows-1 overflow-hidden rounded-lg bg-cover lg:hidden"
          style={{ backgroundImage: `url(${readinessMaskImageProps.src})` }}
        >
          <Image
            localizedAlt="readiness_app_image_alt"
            className="col-start-1 col-end-2 row-start-1 row-end-2 pt-4"
            localizedSrc="home_scenarios_sleep_readiness_ui"
            format="png"
            width={300}
            height={277}
            data-cy="start-day-readiness-image"
          />
        </Motion>
      </div>
      <div className="hidden self-center rounded-lg border border-gray-300 px-10 py-12 lg:block">
        <QuoteIcon className="text-olive-700" />
        <Typography
          className="mt-10 text-3xl"
          font="serif"
          weight="thin"
          variant="unset"
        >
          <RichText messageKey="features_tab_1_quote" />
        </Typography>
        <Typography
          className="mt-4 text-olive-700"
          variant="body-small"
          color="unset"
        >
          Jussi L., {t('oura_member')}
        </Typography>
      </div>
    </MobileFeature>
  );
};

export default StartDay;
